import { NotificationSuccessIcon } from '@tableau/icons/NotificationSuccessIcon';
import {
  ActivitySpinnerSize,
  ActivitySpinnerWidget,
  ButtonType,
  DialogFooterButtonsWidget,
  DialogPositioningBehavior,
  DialogType,
  DialogWidget,
  InlineAlertType,
  InlineAlertWidget,
  ProgressBarWidget,
} from '@tableau/widgets';
import React from 'react';

import { CreateCustomDialogFn, ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { WindowLocationService } from '~commonInfra/services/WindowLocationService';
import { Messages } from '~src/codegen/Localize';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { isValidExtension } from '~src/uploadContent/utils/UploadContentDialogHelper';
import { FileInput } from '~src/userManagement/components/FileInput';

import { toUploadStage } from '../utils/bundleUploadActions';
import {
  BundleUploadController,
  BundleUploadErrorKind,
  BundleUploadPhase,
  BundleUploadState,
  INITIAL_BUNDLE_UPLOAD_STATE,
} from '../utils/BundleUploadController';
import { ImportBundleDialogOpts } from './ImportBundleDialog';
import { UploadBundleDialogStyles } from './UploadBundleDialogStyles';

/** Content relay bundle file types accepted by the picker/dropzone. */
const VALID_BUNDLE_EXTENSIONS = ['.zip'];

const TEST_ID = 'content-relay-upload';

export interface UploadBundleDialogProps {
  onClose: () => void;
}

function siteUrls(): SpacesSiteUrls {
  return new SpacesSiteUrls({ siteUrlName: getSessionManagementServiceInstance().sessionSiteUrlName() });
}

function goToContentRelayImportHistory(onClose: () => void): void {
  WindowLocationService.navigateTo(siteUrls().contentRelayImportsUrl());
  onClose();
}

/**
 * Hands off to the import step (W-24368277) for the staged bundle: closes the upload dialog and opens the
 * import dialog, or goes to Import history when the upload didn't report the bundle.
 */
export function continueToBundleImport(bundleLuid: string | undefined, onClose: () => void): void {
  if (!bundleLuid) {
    goToContentRelayImportHistory(onClose);
    return;
  }
  onClose();
  ReactModalService.openCustom(ImportBundleDialogOpts(bundleLuid));
}

function showErrorToast(kind: BundleUploadErrorKind): void {
  ToasterService.instance.pop({
    type: ToastType.Error,
    text: kind === 'cancel' ? Messages.contentRelayUploadCancelErrorToast() : Messages.contentRelayUploadErrorToast(),
  });
}

/**
 * Destination-side "Import content relay package" upload (W-23846349), on the three-call handoff from
 * W-23797339: the bundle is reserved up front (`beginUpload`), the file is staged through the house
 * chunked-upload (initiate/append), and `triggerUpload` queues the task that hands the bundle to the
 * Content Mobility service. The execution is then polled until the bundle is validated, unpacked and
 * ready to import (W-24368277) — or fails as an invalid bundle.
 *
 * The upload/cancel/retry logic lives in {@link BundleUploadController}; this component only renders
 * its state and the file picker.
 */
export function UploadBundleDialog(props: UploadBundleDialogProps): JSX.Element {
  const [file, setFile] = React.useState<File | undefined>(undefined);
  const [invalidFile, setInvalidFile] = React.useState(false);
  const [upload, setUpload] = React.useState<BundleUploadState>(INITIAL_BUNDLE_UPLOAD_STATE);

  const controllerRef = React.useRef<BundleUploadController | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = new BundleUploadController(setUpload, showErrorToast);
  }
  const controller = controllerRef.current;

  React.useEffect(function() {
    return function() { controller.dispose(); };
  }, [controller]);

  const onFileChosen = function(chosen: File | undefined): void {
    setInvalidFile(false);
    if (!chosen) {
      setFile(undefined);
      return;
    }
    // An empty file can never be a valid package.
    if (!isValidExtension(chosen.name, VALID_BUNDLE_EXTENSIONS) || chosen.size === 0) {
      setFile(undefined);
      setInvalidFile(true);
      return;
    }
    setFile(chosen);
  };

  const goToImportHistory = function(): void {
    goToContentRelayImportHistory(props.onClose);
  };

  const continueToImport = function(): void {
    continueToBundleImport(upload.bundleLuid, props.onClose);
  };

  return (
    <DialogWidget
      type={DialogType.NormalWithTitleBar}
      title={Messages.contentRelayUploadTitle()}
      showCloseButton={true}
      onCloseRequest={props.onClose}
      positioningBehavior={DialogPositioningBehavior.Centered}
      shouldRecenterOnUpdate={true}
      isModal={true}
      dialogWidth={560}
      footerContent={renderFooter(upload.phase, {
        file,
        invalidFile,
        onUpload: function() { if (file) { controller.start(file); } },
        onCancel: function() { controller.cancel(); },
        onClose: props.onClose,
        onTryAgain: function() { controller.reset(); },
        goToImportHistory,
        continueToImport,
      })}
      testId={TEST_ID}
    >
      {renderBody(upload, { file, invalidFile, onFileChosen })}
    </DialogWidget>
  );
}

interface BodyProps {
  file: File | undefined;
  invalidFile: boolean;
  onFileChosen: (file: File | undefined) => void;
}

function renderSpinner(phase: BundleUploadPhase, message: string, file: File | undefined): JSX.Element {
  return (
    <div className={UploadBundleDialogStyles.statusState} data-tb-test-id={`${TEST_ID}-${phase}`}>
      <ActivitySpinnerWidget spinnerSize={ActivitySpinnerSize.Large} alt={message} />
      <div className={UploadBundleDialogStyles.statusMessage}>{message}</div>
      {file ? <div className={UploadBundleDialogStyles.statusSubMessage}>{file.name}</div> : null}
    </div>
  );
}

function renderAlert(phase: BundleUploadPhase, type: InlineAlertType, message: string): JSX.Element {
  return (
    <div className={UploadBundleDialogStyles.resultBlock} data-tb-test-id={`${TEST_ID}-${phase}`}>
      <InlineAlertWidget type={type} testId={`${TEST_ID}-${phase}-alert`}>
        {message}
      </InlineAlertWidget>
    </div>
  );
}

function renderBody(upload: BundleUploadState, props: BodyProps): JSX.Element {
  switch (upload.phase) {
    case 'uploading':
      return (
        <div className={UploadBundleDialogStyles.statusState} data-tb-test-id={`${TEST_ID}-uploading`}>
          <ProgressBarWidget
            className={UploadBundleDialogStyles.progressBar}
            label={props.file ? props.file.name : ''}
            value={upload.uploadPercent}
            max={100}
            message={Messages.contentRelayUploadUploadingMessage()}
          />
        </div>
      );

    case 'triggering':
      return renderSpinner(upload.phase, Messages.contentRelayUploadQueuedMessage(), props.file);

    case 'tracking': {
      const processing = upload.execution && toUploadStage(upload.execution) === 'processing';
      const progress = upload.execution && upload.execution.progress ? upload.execution.progress : 0;
      if (processing && progress > 0) {
        return (
          <div className={UploadBundleDialogStyles.statusState} data-tb-test-id={`${TEST_ID}-tracking`}>
            <ProgressBarWidget
              className={UploadBundleDialogStyles.progressBar}
              label={props.file ? props.file.name : ''}
              value={progress}
              max={100}
              message={Messages.contentRelayUploadProcessingMessage()}
            />
          </div>
        );
      }
      return renderSpinner(
        upload.phase,
        processing ? Messages.contentRelayUploadProcessingMessage() : Messages.contentRelayUploadQueuedMessage(),
        props.file
      );
    }

    case 'cancelling':
      return renderSpinner(upload.phase, Messages.contentRelayUploadCancellingMessage(), props.file);

    case 'ready':
      return (
        <div className={UploadBundleDialogStyles.statusState} data-tb-test-id={`${TEST_ID}-ready`}>
          <NotificationSuccessIcon size={24} />
          <div className={UploadBundleDialogStyles.statusMessage}>{Messages.contentRelayUploadReadyTitle()}</div>
          <div className={UploadBundleDialogStyles.statusSubMessage}>{Messages.contentRelayUploadReadyMessage()}</div>
        </div>
      );

    case 'invalidBundle':
      return renderAlert(upload.phase, InlineAlertType.Error, Messages.contentRelayUploadInvalidBundleMessage());

    case 'uploadFailed':
      return renderAlert(upload.phase, InlineAlertType.Error, Messages.contentRelayUploadTransferFailedMessage());

    case 'statusUnavailable':
      return renderAlert(upload.phase, InlineAlertType.Warning, Messages.contentRelayUploadStatusUnavailableMessage());

    case 'cancelled':
      return renderAlert(upload.phase, InlineAlertType.Info, Messages.contentRelayUploadCancelledMessage());

    default:
      // pick
      return (
        <div data-tb-test-id={`${TEST_ID}-pick`}>
          <FileInput
            accept={VALID_BUNDLE_EXTENSIONS.join(',')}
            allowFileDrop={true}
            handleFileInput={props.onFileChosen}
            selectedFileName={props.file ? props.file.name : undefined}
            testId={`${TEST_ID}-file`}
          />
          {props.invalidFile ? (
            <div className={UploadBundleDialogStyles.alert}>
              <InlineAlertWidget type={InlineAlertType.Error} testId={`${TEST_ID}-invalid-file-alert`}>
                {Messages.contentRelayUploadInvalidFileMessage()}
              </InlineAlertWidget>
            </div>
          ) : null}
        </div>
      );
  }
}

interface FooterProps {
  file: File | undefined;
  invalidFile: boolean;
  onUpload: () => void;
  onCancel: () => void;
  onClose: () => void;
  onTryAgain: () => void;
  goToImportHistory: () => void;
  continueToImport: () => void;
}

function renderFooter(phase: BundleUploadPhase, props: FooterProps): JSX.Element {
  switch (phase) {
    case 'uploading':
    case 'triggering':
      // Cancelling before the upload is tracked returns to the picker straight away.
      return (
        <DialogFooterButtonsWidget
          buttons={[{ buttonType: ButtonType.Outline, onClick: props.onCancel, children: Messages.cancel(), testId: `${TEST_ID}-cancel-upload` }]}
        />
      );

    case 'tracking':
      // Close leaves the upload running server-side; Cancel upload stops it.
      return (
        <DialogFooterButtonsWidget
          buttons={[
            { buttonType: ButtonType.Outline, onClick: props.onCancel, children: Messages.contentRelayUploadCancel(), testId: `${TEST_ID}-cancel-processing` },
            { buttonType: ButtonType.Primary, onClick: props.onClose, children: Messages.close(), testId: `${TEST_ID}-tracking-close` },
          ]}
        />
      );

    case 'cancelling':
      return (
        <DialogFooterButtonsWidget
          buttons={[{ buttonType: ButtonType.Outline, onClick: props.onClose, children: Messages.close(), testId: `${TEST_ID}-cancelling-close` }]}
        />
      );

    case 'ready':
      return (
        <DialogFooterButtonsWidget
          buttons={[
            { buttonType: ButtonType.Outline, onClick: props.onClose, children: Messages.done(), testId: `${TEST_ID}-done` },
            { buttonType: ButtonType.Primary, onClick: props.continueToImport, children: Messages.contentRelayUploadContinueToImport(), testId: `${TEST_ID}-continue` },
          ]}
        />
      );

    case 'statusUnavailable':
      // The upload may still finish server-side, so point to Import history rather than a retry.
      return (
        <DialogFooterButtonsWidget
          buttons={[
            { buttonType: ButtonType.Outline, onClick: props.onClose, children: Messages.close(), testId: `${TEST_ID}-result-close` },
            { buttonType: ButtonType.Primary, onClick: props.goToImportHistory, children: Messages.contentRelayUploadGoToImportHistory(), testId: `${TEST_ID}-import-history` },
          ]}
        />
      );

    case 'invalidBundle':
    case 'uploadFailed':
    case 'cancelled':
      return (
        <DialogFooterButtonsWidget
          buttons={[
            { buttonType: ButtonType.Outline, onClick: props.onClose, children: Messages.close(), testId: `${TEST_ID}-result-close` },
            { buttonType: ButtonType.Primary, onClick: props.onTryAgain, children: Messages.tryAgain(), testId: `${TEST_ID}-try-again` },
          ]}
        />
      );

    default:
      // pick
      return (
        <DialogFooterButtonsWidget
          buttons={[
            { buttonType: ButtonType.Outline, onClick: props.onClose, children: Messages.cancel(), testId: `${TEST_ID}-cancel` },
            { buttonType: ButtonType.Primary, onClick: props.onUpload, disabled: !props.file || props.invalidFile, children: Messages.upload(), testId: `${TEST_ID}-upload` },
          ]}
        />
      );
  }
}

/**
 * Options for ReactModalService.openCustom — the dialog manages its own phases and closes itself via
 * the passed close callback. Opened from the Content Relays "New" menu and the Explore "+ New" menu.
 */
export function UploadBundleDialogOpts(): CreateCustomDialogFn {
  return function(closeAndDismiss) {
    return <UploadBundleDialog onClose={closeAndDismiss.close} />;
  };
}
